/**
 * Full-screen state for ArTable (SYD-2437).
 *
 * One table at a time may claim the viewport; `tableKey` (from
 * `formStore.tableKey(parentPath, tableId)`) is that claim, `null` when no
 * table is full screen. Consumers: `ArTableBody` (container style, header
 * sticky offset, the `table.toggleFullScreen` and `table.exitFullScreen` chords
 * on its own container, and `release` on unmount), `ArTableControls` (expand
 * button + controls sticky offset).
 *
 * Invariants: the flag flip is the only mechanism — no portal, no remount, no
 * body/document mutation, so an unmount while full screen can never leak a
 * global lock. `enter` captures the window scroll BEFORE setting the flag
 * (going `position: fixed` shortens the document and the browser clamps
 * `scrollY` immediately after the re-render). `exit` is the user-facing close
 * and restores that scroll in a `requestAnimationFrame`, which runs after
 * Valtio's microtask re-render has returned the container to flow and the
 * document has regained its height. `release` is the lifecycle close the
 * holding table calls when it unmounts: it drops the claim without restoring,
 * because the page the scroll was captured on is already gone.
 */

import { proxy } from 'valtio';
import { FORM_PAGE_CSS_VARS } from '@/components/atoms/FORM_PAGE_CSS_VARS';

/**
 * Sticky offset of the page chrome above a table when the document is the
 * scrollport: navbar plus form-page header, measured from the document top. It
 * is a CSS value reading `--page-chrome-height`, which `FormPageContent` measures,
 * because the form header wraps onto several lines on a phone. 118px (Navb 65
 * plus header 53, the desktop value) is the fallback for pages that never set it.
 * In full screen the container is the scrollport and this offset becomes 0.
 */
export const PAGE_CHROME_STICKY_TOP = `var(${FORM_PAGE_CSS_VARS.pageChromeHeight}, 118px)`;

export type ArTableFullScreenState = {
    tableKey: string | null;
};

export const arTableFullScreenState = proxy<ArTableFullScreenState>({
    tableKey: null,
});

const savedScroll = { x: 0, y: 0 };

const enter = (tableKey: string) => {
    if (arTableFullScreenState.tableKey === null) {
        savedScroll.x = window.scrollX;
        savedScroll.y = window.scrollY;
    }
    arTableFullScreenState.tableKey = tableKey;
};

const exit = () => {
    if (arTableFullScreenState.tableKey === null) return;
    arTableFullScreenState.tableKey = null;
    const { x, y } = savedScroll;
    requestAnimationFrame(() => window.scrollTo(x, y));
};

const release = (tableKey: string) => {
    if (arTableFullScreenState.tableKey !== tableKey) return;
    arTableFullScreenState.tableKey = null;
};

const toggle = (tableKey: string) => {
    if (arTableFullScreenState.tableKey === tableKey) {
        exit();
    } else {
        enter(tableKey);
    }
};

export const arTableFullScreenActions = Object.freeze({
    enter,
    exit,
    release,
    toggle,
});

export type ArTableFullScreenActions = typeof arTableFullScreenActions;
